import React, { useCallback, useMemo } from 'react'
import { uniq } from 'lodash'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Box, Typography, Button, Stack, IconButton } from '@mui/material'
import { makeStyles } from '@mui/styles'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { randomId } from '@mui/x-data-grid-generator'

import BasicDataGrid from 'components/generic/datagrid'
import MaterialCodeCell from 'components/generic/datagrid/cells/MaterialCodeCell'
import EditableCell from 'components/generic/datagrid/cells/EditableCell'

import { calculateTotalWeight, calculateTotalValue } from 'utils/data-manipulation/common'
import { MEASURE_UNIT_ARRAY, ROUNDING_PRECISIONS } from 'utils/constants'
import { setDecimalPrecision } from 'utils/helpers'
import { roundTo } from 'utils/data-manipulation/valueFormatter'
import { Tooltip } from '@mui/material_modern'
import { Lock as LockIcon } from '@mui/icons-material_modern'

const useStyles = makeStyles(theme => ({
  root: {
    // remove header padding to align headers with column values
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeader': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-cell': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-columnSeparator': {
      visibility: 'hidden',
    },
    // replace default hover / active-row styling from datagrid
    '& .MuiDataGrid-row': {
      '&.total-row': {
        backgroundColor: '#009F4D14',
        '&:hover': {
          backgroundColor: '#009F4D14',
        },
      },
      '&:not(.total-row)': {
        '& .grey-cell': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
        '&.Mui-selected': {
          backgroundColor: 'inherit',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
          },
        },
        '&:not(.Mui-selected)': {
          backgroundColor: 'inherit',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },
  },
}))

const ContractMaterialsList = ({
  isUpdate,
  duplicateContractTypeOrderId,
  isView,
  priceType,
  materials,
  loading,
  apiRef,
  onEditCallback,
  control,
  pricePrepend,
  isTransfer,
}) => {
  const classes = useStyles()

  const { disableUmDropdowns } = useSelector(state => state.settings?.settings?.purchaseSettings?.inboundTickets || {})
  const { enableTransferTicketsConsumerWeightsFF } = useSelector(state => state.featureFlags?.variables || {})

  const createOrUpdateTotalRow = () => {
    apiRef.current?.updateRows([{ id: 'total', code: 'Total' }])
    const rows = Array.from(apiRef.current?.getRowModels().values() || []).slice(0, -1)

    const uniqueUMs = uniq(rows.map(r => r.um?.toUpperCase()))
    let totalTargetQuantity
    let totalFulfilledQuantity

    if (uniqueUMs.length > 1) {
      totalTargetQuantity = 'Mixed'
      totalFulfilledQuantity = 'Mixed'
    } else {
      totalTargetQuantity = calculateTotalWeight(rows)
      totalFulfilledQuantity = calculateTotalWeight(rows, { fieldName: 'fulfilledWeight' })
    }
    const totalValue = calculateTotalValue(rows)

    apiRef.current?.updateRows([
      {
        id: 'total',
        code: 'Total',
        fulfilledWeight: totalFulfilledQuantity,
        targetWeight: totalTargetQuantity,
        value: totalValue,
      },
    ])
  }

  const handleDeleteClick = useCallback(
    id => event => {
      event.stopPropagation()

      apiRef.current?.updateRows([{ id, _action: 'delete' }])

      onEditCallback()
      createOrUpdateTotalRow()
    },
    [],
  )

  const materialColumns = useMemo(() => {
    const allColumns = [
      {
        field: 'code',
        headerName: 'Material',
        flex: 4.5,
        renderCell: params => (
          <MaterialCodeCell
            cellData={params}
            isView={isView}
            priceType={priceType}
            apiRef={apiRef}
            createOrUpdateTotalRow={createOrUpdateTotalRow}
            onEditCallback={onEditCallback}
          />
        ),
        sortable: false,
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 3.5,
        hideWhenPrinting: false,
        editable: !isView,
        sortable: false,
      },
      {
        field: 'overrideName',
        headerName: 'Override Name',
        flex: 3.5,
        hideWhenPrinting: true,
        editable: !isView,
        sortable: false,
        renderHeader: () => {
          const headerName = 'Override Name'
          if (enableTransferTicketsConsumerWeightsFF && isTransfer) {
            return (
              <div className="MuiDataGrid-columnHeaderTitle" aria-label={headerName}>
                <Tooltip title={`${headerName}s for transfers are disabled`} placement="top" arrow>
                  <IconButton size="small" edge="start" disabled>
                    <LockIcon />
                  </IconButton>
                  <>{headerName}</>
                </Tooltip>
              </div>
            )
          }
          return (
            <div className="MuiDataGrid-columnHeaderTitle" aria-label={headerName}>
              {headerName}
            </div>
          )
        },
      },
      {
        field: 'fulfilledWeight',
        headerName: 'Fulfilled Quantity',
        flex: 2,
        headerAlign: 'right',
        align: 'right',
        type: 'number',
        valueParser: value => {
          if (!value || value < 0) {
            return 0
          }
          return parseInt(value, 10)
        },
        editable: false,
        sortable: false,
      },
      {
        field: 'targetWeight',
        headerName: 'Target Quantity',
        flex: 2,
        headerAlign: 'right',
        align: 'right',
        type: 'number',
        valueParser: value => {
          if (!value || value < 0) {
            return 0
          }
          return parseInt(value, 10)
        },
        editable: !isView,
        sortable: false,
      },
      {
        field: 'price',
        headerName: disableUmDropdowns ? 'Price/UM' : 'Price',
        flex: 2,
        headerAlign: 'right',
        align: 'right',
        type: 'number',
        valueParser: value => {
          if (!value) {
            return 0
          }
          return parseFloat(value)
        },
        valueFormatter: params => {
          const { value } = params
          return value || value === 0 ? `${pricePrepend}${setDecimalPrecision(value)}` : ''
        },
        editable: !isView,
        sortable: false,
      },
      {
        field: 'slash',
        headerName: '',
        align: 'center',
        width: 4,
        renderCell: ({ row }) => (row?.id === 'total' ? '' : '/'),
        sortable: false,
      },
      {
        field: 'um',
        headerName: 'UM',
        flex: 1.5,
        headerAlign: 'left',
        align: 'left',
        sortable: false,
        renderCell: params => {
          const { row } = params
          if (row.id === 'total') {
            return null
          }
          return (
            <EditableCell
              name={`${row.id}-um`}
              label=""
              data-test={`${row.id}-um`}
              control={control}
              defaultValue={row.um?.toLowerCase()}
              cellType="SELECT"
              size="small"
              options={MEASURE_UNIT_ARRAY}
              isEdit={!isView && apiRef.current.getRowMode(row?.id) === 'edit'}
              onChange={event => {
                const { value } = event.target
                apiRef.current?.updateRows([{ ...row, um: value }])
                createOrUpdateTotalRow()
              }}
              variant="standard"
              InputProps={{ sx: { fontSize: '0.875rem' } }}
            />
          )
        },
      },
      {
        field: 'value',
        headerName: 'Value',
        flex: 2,
        headerAlign: 'right',
        cellClassName: !isView && 'grey-cell',
        align: 'right',
        valueGetter: params => {
          const { targetWeight = 0, price = 0, value: rowValue } = params.row
          const value = roundTo(rowValue || targetWeight * price, ROUNDING_PRECISIONS.COST)
          return `${pricePrepend}${setDecimalPrecision(value, { decimalPrecisionOverride: 2 })}`
        },
        sortable: false,
      },
      {
        field: 'actions',
        headerName: '',
        renderCell: params => {
          const { row } = params
          return row.id === 'total' ? null : (
            <div>
              <IconButton
                color="error"
                size="small"
                sx={{
                  fill: 'red',
                }}
                aria-label="delete"
                disabled={!row.isNew && !duplicateContractTypeOrderId}
                onClick={handleDeleteClick(params.row.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          )
        },
        headerAlign: 'center',
        align: 'center',
        hideWhenPrinting: true,
        sortable: false,
      },
    ]

    return allColumns.filter(column => {
      if (disableUmDropdowns && (column.field === 'um' || column.field === 'slash')) {
        return false
      }
      if (isView && column.field === 'actions') {
        return false
      }
      if (!isView && column.field === 'fulfilledWeight') {
        return false
      }
      return true
    })
  }, [
    isView,
    priceType,
    apiRef,
    createOrUpdateTotalRow,
    onEditCallback,
    enableTransferTicketsConsumerWeightsFF,
    isTransfer,
    control,
    pricePrepend,
    disableUmDropdowns,
    handleDeleteClick,
    duplicateContractTypeOrderId,
  ])

  const handleCellEditPropsChange = useCallback(({ id, field, props: { value } }) => {
    const row = apiRef.current?.getRow(id)
    const isNumberField = field === 'targetWeight' || field === 'price' || field === 'fulfilledWeight'

    if (isNumberField && value < 0) {
      apiRef.current?.updateRows([{ ...row, [field]: 0 }])
    } else {
      apiRef.current?.updateRows([{ ...row, [field]: value }])
    }

    createOrUpdateTotalRow()
  }, [])

  const onClickNewMaterial = useCallback(() => {
    const id = randomId()
    apiRef.current?.updateRows([{ id: 'total', _action: 'delete' }])
    apiRef.current?.updateRows([{ id, isNew: true }])

    apiRef.current?.scrollToIndexes({
      rowIndex: apiRef.current?.getRowsCount() - 1,
    })

    onEditCallback()
    createOrUpdateTotalRow()

    apiRef.current?.setCellFocus(id, 'code')
  }, [apiRef])

  const onFirstRowAdd = useCallback(() => {
    if (!isUpdate && !isView && !duplicateContractTypeOrderId && apiRef && apiRef.current?.getRowsCount?.() === 0) {
      setTimeout(() => {
        const id = randomId()
        apiRef.current?.updateRows([{ id, isNew: true }])

        apiRef.current?.scrollToIndexes({
          rowIndex: apiRef.current?.getRowsCount() - 1,
        })

        apiRef.current?.setCellFocus(id, 'code')
      }, 200)
    }
    setTimeout(createOrUpdateTotalRow, 200)
  }, [apiRef, isUpdate, duplicateContractTypeOrderId, isView])

  return (
    <>
      <Box p={3} data-testid="materials-list">
        <Stack direction="row" mb={1}>
          <Typography variant="h6" gutterBottom color="textSecondary">
            Materials
          </Typography>
        </Stack>
        <BasicDataGrid
          className={classes.root}
          density="compact"
          height="auto"
          data-testid="contract-materials-list"
          loading={loading}
          rows={materials}
          columns={materialColumns}
          onEditCellPropsChange={handleCellEditPropsChange}
          onRowEditCommit={onEditCallback}
          onCellDoubleClick={({ row }) => {
            apiRef?.current.getAllRowIds().forEach(rid => {
              if (rid !== row?.id) apiRef.current.setRowMode(rid, 'view')
            })
            apiRef?.current.setRowMode(row?.id, 'edit')
          }}
          apiRef={apiRef}
          editMode="row"
          disableColumnMenu
          hideFooter
          isCellEditable={params => params.row.id !== 'total'}
          getRowClassName={params => (params.row.id === 'total' ? 'total-row' : 'normal-row')}
          components={{
            LoadingOverlay: () => null,
          }}
          onMounted={onFirstRowAdd}
        />
        {isView ? null : (
          <Box mt={1}>
            <Button startIcon={<AddIcon />} onClick={onClickNewMaterial} data-testid="active-material-new-button">
              Add New Material
            </Button>
          </Box>
        )}
      </Box>
    </>
  )
}

ContractMaterialsList.propTypes = {
  isUpdate: PropTypes.bool,
  duplicateContractTypeOrderId: PropTypes.string,
  isView: PropTypes.bool,
  priceType: PropTypes.string,
  materials: PropTypes.array,
  loading: PropTypes.bool,
  apiRef: PropTypes.object.isRequired,
  onEditCallback: PropTypes.func,
  control: PropTypes.object,
  pricePrepend: PropTypes.string,
  isTransfer: PropTypes.bool,
}

ContractMaterialsList.defaultProps = {
  isUpdate: false,
  duplicateContractTypeOrderId: '',
  isView: false,
  priceType: 'buyPrice',
  materials: [],
  loading: false,
  onEditCallback: () => {},
  control: null,
  pricePrepend: '$',
  isTransfer: false,
}

export default ContractMaterialsList
